/**
 * SIMULADOR DE SEMANAS (Trello «Crear nueva pestaña Simulador de semanas», 30-09; dry-run 01-10).
 *
 * Se elige un cliente (de prueba o real), una fecha de inicio y el día que se quiere mirar —por
 * fecha o por semana y día del plan— y el backend (core/simulador_semanas) evalúa ese día con
 * las MISMAS funciones que usa la app: qué reglas se cumplen, por qué, y qué acciones saldrían.
 * Es un DRY RUN: no guarda nada, no manda nada y no llama a ActiveCampaign.
 *
 * Desde el 08-10 enseña el TEXTO de cada correo que saldría y, donde el servidor lo permite
 * (nunca en producción), una casilla para mandarlo de prueba al buzón de pruebas. El texto lo
 * redacta el servidor; de aquí solo sale el indicador `mandar_correos`.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
    ChevronLeft, ChevronRight, FlaskConical, Play, Search, CheckCircle2, XCircle, Circle, Users, User,
    Dumbbell, ShieldCheck, Send, AlertTriangle,
} from 'lucide-react';
import { PlanBadge } from './ClientDashboard';
import SimuladorPasoAPaso from '../components/SimuladorPasoAPaso';
import { CasillaDeCorreo, EnvioDePrueba, TextoDelCorreo } from '../components/CorreosDelSimulador';
import { mensajeDeError } from '../lib/mensajeDeError';
import { seGastaLaCasilla } from '../lib/correosDelSimulador';

const CALCULADORA = { personalizado: 'Con entrenador', autogestion: 'Autogestión', sin_ajuste: 'Sin ajuste' };
const RUTINA = { personalizada: 'Personalizada', del_mes: 'Del mes', opcional: 'Opcional', ninguna: 'Sin rutina' };
const PARA = {
    cliente: { texto: 'Cliente', icono: User, clase: 'bg-[#FF671F]/15 text-[#FF671F]' },
    entrenador: { texto: 'Entrenador', icono: Dumbbell, clase: 'bg-sky-500/15 text-sky-400' },
    equipo: { texto: 'Equipo', icono: Users, clase: 'bg-violet-500/15 text-violet-300' },
};
const ENTRADA = 'bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#FF671F]';

const Paso = ({ n, titulo, children, derecha }) => (
    <Card className="bg-[#111] border-[#222]">
        <CardContent className="p-4 sm:p-5">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h2 className="text-white font-bold uppercase tracking-wide flex items-center gap-2"
                    style={{ fontFamily: 'Barlow Condensed', fontSize: 20 }}>
                    <span className="w-6 h-6 rounded-full bg-[#FF671F] text-white text-sm flex items-center justify-center">{n}</span>
                    {titulo}
                </h2>
                {derecha}
            </div>
            {children}
        </CardContent>
    </Card>
);

const Selector = ({ etiqueta, valor, onMenos, onMas, puedeMenos, puedeMas, testid }) => (
    <div>
        <p className="text-xs uppercase tracking-wider text-white/40 mb-1.5">{etiqueta}</p>
        <div className="flex items-center gap-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg p-1 w-fit">
            <button type="button" onClick={onMenos} disabled={!puedeMenos} aria-label={`${etiqueta} anterior`}
                className="w-9 h-9 rounded-md flex items-center justify-center text-white/70 hover:bg-[#1F1F1F] disabled:opacity-25">
                <ChevronLeft className="w-5 h-5" />
            </button>
            <span data-testid={testid} className="min-w-[108px] text-center text-white font-semibold">{valor}</span>
            <button type="button" onClick={onMas} disabled={!puedeMas} aria-label={`${etiqueta} siguiente`}
                className="w-9 h-9 rounded-md flex items-center justify-center text-white/70 hover:bg-[#1F1F1F] disabled:opacity-25">
                <ChevronRight className="w-5 h-5" />
            </button>
        </div>
    </div>
);

const Chip = ({ children, apagado }) => (
    <span className={`inline-block text-xs px-2 py-0.5 rounded-full border mr-1 mb-1 ${apagado
        ? 'border-[#2A2A2A] text-white/30' : 'border-[#333] text-white/80 bg-[#1A1A1A]'}`}>{children}</span>
);

const Pestanas = ({ valor, opciones, onCambio, testid }) => (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
        {opciones.map(([k, t]) => (
            <button key={k} type="button" onClick={() => onCambio(k)}
                className={`text-sm px-3 py-1.5 rounded-lg ${valor === k ? 'bg-[#FF671F] text-white' : 'bg-[#1A1A1A] text-white/60 hover:text-white'}`}>{t}</button>
        ))}
    </div>
);

const fechaLarga = (iso) => (iso
    ? new Date(`${iso}T12:00:00`).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : '—');

const sumarDias = (iso, n) => {
    const d = new Date(`${iso}T12:00:00`);
    d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
};

const leer = (clave, defecto) => { try { return localStorage.getItem(clave) || defecto; } catch { return defecto; } };
const guardar = (clave, valor) => { try { localStorage.setItem(clave, valor); } catch { /* nada */ } };

const AdminSimuladorPage = () => {
    const { api } = useAuth();
    const [datos, setDatos] = useState(null);
    const [elegido, setElegido] = useState(null);
    // De prueba o reales: vale para los dos modos (01-10).
    const [origen, setOrigen] = useState(() => (leer('simulador-origen', 'prueba') === 'real' ? 'real' : 'prueba'));
    const [reales, setReales] = useState([]);
    const [buscandoReales, setBuscandoReales] = useState(false);
    const [buscar, setBuscar] = useState('');
    const [inicio, setInicio] = useState('');
    const [porFecha, setPorFecha] = useState(() => leer('simulador-por', 'semana') === 'fecha');
    const [fecha, setFecha] = useState('');
    const [semana, setSemana] = useState(1);
    const [dia, setDia] = useState(1);
    const [mandaReportes, setMandaReportes] = useState(false);
    // La casilla del correo de prueba: apagada al entrar, y no se guarda en el navegador.
    const [mandarCorreos, setMandarCorreos] = useState(false);
    const [resultado, setResultado] = useState(null);
    const [simulando, setSimulando] = useState(false);
    // Dos maneras de mirarlo: un día concreto de un cliente, o la tabla entera paso a paso (César, 30-09).
    const [modo, setModo] = useState(() => leer('simulador-modo', 'dia'));
    const cambiarModo = (m) => { setModo(m); guardar('simulador-modo', m); };
    const cajaTabla = useRef(null);
    const pasoMomento = useRef(null);

    useEffect(() => {
        const fila = elegido && cajaTabla.current?.querySelector(`[data-sim="${elegido.id}"]`);
        const caja = cajaTabla.current;
        if (!caja || !fila || !caja.clientHeight) return;
        const arriba = fila.offsetTop - 40;
        if (arriba < caja.scrollTop || fila.offsetTop + fila.offsetHeight > caja.scrollTop + caja.clientHeight) {
            caja.scrollTop = Math.max(0, arriba);
        }
    }, [elegido]);

    useEffect(() => {
        api.get('/admin/simulador-semanas/clientes')
            .then((res) => {
                setDatos(res.data);
                const gold = res.data.clientes.find((c) => c.id === 'sim-gold-2') || res.data.clientes[0] || null;
                setElegido(gold);
                setInicio(gold?.fecha_inicio || res.data.ancla);
                setFecha(gold?.fecha_inicio || res.data.ancla);
            })
            .catch((e) => { console.error('[simulador]', e); toast.error('No se pudieron cargar los clientes del simulador'); });
    }, [api]);

    // Los clientes reales se leen del servidor (solo lectura). Sin texto, la cartera activa (la
    // misma lista que «Simular todos»); con texto, se busca en toda la base, con una pausa al escribir.
    useEffect(() => {
        if (origen !== 'real') return undefined;
        const t0 = buscar.trim();
        const q = t0.length < 2 ? '' : t0;
        setBuscandoReales(true);
        const t = setTimeout(() => {
            api.get('/admin/simulador-semanas/clientes-reales', { params: { q } })
                .then((res) => setReales(res.data.clientes || []))
                .catch((e) => { console.error('[simulador]', e); toast.error('No se pudo buscar el cliente'); })
                .finally(() => setBuscandoReales(false));
        }, 300);
        return () => clearTimeout(t);
    }, [api, buscar, origen]);

    const clientes = useMemo(() => {
        if (origen === 'real') return reales;
        const q = buscar.trim().toLowerCase();
        return (datos?.clientes || []).filter((c) => !q
            || `${c.nombre} ${c.plan_nombre} ${c.plan} ${c.variante}`.toLowerCase().includes(q));
    }, [datos, buscar, origen, reales]);

    const semanasCiclo = elegido?.semanas_ciclo || 12;
    // Se deja llegar dos semanas más allá del ciclo, para ver qué pasa cuando se acaba.
    const semanaMax = Math.min(datos?.semana_maxima || 60, semanasCiclo + 2);
    // La fecha que sale de semana y día, para enseñarla antes de simular (día 1 = el de inicio).
    const fechaDeSemana = inicio ? sumarDias(inicio, 7 * (semana - 1) + (dia - 1)) : '';
    const fechaElegida = porFecha ? fecha : fechaDeSemana;

    const limpiar = () => setResultado(null);
    const elegir = (c) => {
        setElegido(c);
        setResultado(null);
        setInicio(c.fecha_inicio || datos?.ancla || '');
        setSemana((s) => Math.min(s, (c.semanas_ciclo || 12) + 2));
        // Elegido el cliente, lo siguiente es el momento: se baja a la sección 2 y el foco va a
        // su primer campo (la fecha de inicio).
        setTimeout(() => {
            pasoMomento.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            pasoMomento.current?.querySelector('#sim-inicio')?.focus({ preventScroll: true });
        }, 0);
    };
    const cambiarOrigen = (o) => { setOrigen(o); guardar('simulador-origen', o); setBuscar(''); setResultado(null); };
    const cambiarPor = (f) => {
        setPorFecha(f === 'fecha');
        guardar('simulador-por', f);
        if (f === 'fecha' && fechaDeSemana) setFecha(fechaDeSemana);
        limpiar();
    };

    const simular = async () => {
        if (!elegido) return;
        if (porFecha && !fecha) { toast.error('Elige la fecha simulada'); return; }
        setSimulando(true);
        try {
            const cuerpo = {
                ...(elegido.origen === 'real' ? { cliente_real: elegido.id.replace(/^real:/, '') } : { cliente: elegido.id }),
                fecha_inicio: inicio || null,
                manda_reportes: mandaReportes,
                ...(mandarCorreos && datos?.correo_de_prueba?.se_puede ? { mandar_correos: true } : {}),
                ...(porFecha ? { fecha } : { semana, dia }),
            };
            const res = await api.post('/admin/simulador-semanas/simular', cuerpo);
            setResultado(res.data);
            // La casilla vale para un envío: quien quiera otro, la vuelve a marcar.
            if (seGastaLaCasilla(res.data.correos_mandados)) setMandarCorreos(false);
        } catch (e) {
            console.error('[simulador]', e);
            toast.error(mensajeDeError(e, 'No se pudo simular ese día'));
        } finally {
            setSimulando(false);
        }
    };

    if (!datos) {
        return <div className="p-6 text-white/50">Cargando el simulador…</div>;
    }

    return (
        <div className="p-4 sm:p-6 space-y-5 max-w-[1400px]" data-testid="simulador-semanas">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <h1 className="text-3xl sm:text-4xl font-bold text-white uppercase" style={{ fontFamily: 'Barlow Condensed' }}>
                    Simulador de semanas
                </h1>
                <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400 border border-emerald-500/30 bg-emerald-500/10 rounded-full px-3 py-1">
                    <FlaskConical className="w-3.5 h-3.5" />
                    {mandarCorreos ? 'Simulación: no guarda nada; solo sale el correo de prueba' : 'Solo simulación: no envía ni guarda nada'}
                </span>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                <div className="flex items-center gap-2">
                    <span className="text-xs uppercase tracking-wider text-white/40">Clientes</span>
                    <Pestanas testid="sim-origen" valor={origen} onCambio={cambiarOrigen}
                        opciones={[['prueba', 'De prueba'], ['real', 'Reales']]} />
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs uppercase tracking-wider text-white/40">Modo</span>
                    <Pestanas testid="simulador-modos" valor={modo} onCambio={cambiarModo}
                        opciones={[['dia', 'Simular un cliente'], ['paso', 'Simular todos']]} />
                </div>
            </div>

            {modo === 'paso' ? (
                <SimuladorPasoAPaso key={origen} origen={origen} correoDePrueba={datos.correo_de_prueba}
                    mandarCorreos={mandarCorreos} onMandarCorreos={setMandarCorreos} />
            ) : (<>
            <Paso n={1} titulo="Elige un cliente" derecha={
                <div className="w-full sm:w-auto">
                    <div className="relative w-full sm:w-[240px]">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                        <input value={buscar} onChange={(e) => setBuscar(e.target.value)} data-testid="sim-buscar"
                            placeholder={origen === 'real' ? 'Nombre o correo…' : 'Buscar plan o cliente…'}
                            className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF671F]" />
                    </div>
                </div>
            }>
                {(origen !== 'real' || buscar.trim().length < 2) && (
                    <p className="text-xs text-white/40 mb-3">
                        {origen === 'real'
                            ? `${clientes.length} clientes activos con plan`
                            : `${datos.clientes.length} clientes de prueba`}
                    </p>
                )}
                {origen === 'real' && buscandoReales && clientes.length === 0 ? (
                    <p className="text-sm text-white/40 py-6 text-center">Cargando clientes…</p>
                ) : origen === 'real' && !buscandoReales && clientes.length === 0 ? (
                    <p className="text-sm text-white/40 py-6 text-center">Ningún cliente con ese nombre o correo.</p>
                ) : (<>
                    <div className="sm:hidden space-y-2 max-h-[420px] overflow-auto" data-testid="sim-lista-movil">
                        {clientes.map((c) => {
                            const activo = elegido?.id === c.id;
                            return (
                                <button type="button" key={c.id} onClick={() => elegir(c)}
                                    className={`w-full text-left rounded-lg border p-3 ${activo ? 'border-[#FF671F] bg-[#FF671F]/10' : 'border-[#222] bg-[#0E0E0E]'}`}>
                                    <div className="flex items-center justify-between gap-2">
                                        <p className="text-white font-medium">{c.nombre}</p>
                                        <PlanBadge plan={c.plan} planName={c.plan_nombre} />
                                    </div>
                                    <p className="text-xs text-white/45 mt-1">
                                        {c.email || c.variante} · inicio {c.fecha_inicio} · {CALCULADORA[c.calculadora] || c.calculadora}
                                        {' · '}{c.reportes.length ? c.reportes.join(' + ') : 'sin reportes'}
                                    </p>
                                </button>
                            );
                        })}
                    </div>
                    <div ref={cajaTabla} className="hidden sm:block overflow-auto max-h-[420px] rounded-lg border border-[#222] relative">
                        <table className="w-full text-sm min-w-[720px]">
                            <thead className="sticky top-0 bg-[#141414] z-10">
                                <tr className="text-left text-[11px] uppercase tracking-wider text-white/40">
                                    <th className="px-2.5 py-2.5 font-medium">Cliente</th>
                                    <th className="px-2.5 py-2.5 font-medium">Plan</th>
                                    <th className="px-2.5 py-2.5 font-medium">Inicio</th>
                                    <th className="px-2.5 py-2.5 font-medium">Macros</th>
                                    <th className="px-2.5 py-2.5 font-medium">Rutina</th>
                                    <th className="px-2.5 py-2.5 font-medium">Reportes</th>
                                    <th className="px-2.5 py-2.5 font-medium">Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {clientes.map((c) => {
                                    const activo = elegido?.id === c.id;
                                    return (
                                        <tr key={c.id} onClick={() => elegir(c)} data-testid={`sim-cliente-${c.id}`} data-sim={c.id}
                                            className={`border-t border-[#1E1E1E] cursor-pointer ${activo ? 'bg-[#FF671F]/10' : 'hover:bg-[#1A1A1A]'}`}>
                                            <td className={`px-2.5 py-2.5 ${activo ? 'border-l-2 border-[#FF671F]' : 'border-l-2 border-transparent'}`}>
                                                <p className="text-white font-medium">{c.nombre}</p>
                                                <p className="text-xs text-white/40">{c.email || c.variante}{c.es_prueba ? ' · prueba' : ''}</p>
                                            </td>
                                            <td className="px-2.5 py-2.5">
                                                <PlanBadge plan={c.plan} planName={c.plan_nombre} />
                                                <p className="text-[11px] text-white/30 mt-1">
                                                    {c.tipo_ciclo === 'mensual' ? 'mensual' : c.semanas_ciclo ? `${c.semanas_ciclo} semanas` : 'sin ciclo'}
                                                    {c.plan_estado && c.plan_estado !== 'activo' ? ` · ${c.plan_estado}` : ''}
                                                </p>
                                            </td>
                                            <td className="px-2.5 py-2.5 text-white/70 whitespace-nowrap">{c.fecha_inicio}</td>
                                            <td className="px-2.5 py-2.5 text-white/70 whitespace-nowrap">{CALCULADORA[c.calculadora] || c.calculadora}</td>
                                            <td className="px-2.5 py-2.5">
                                                <p className="text-white/80">{RUTINA[c.rutina] || c.rutina}</p>
                                                <p className="text-xs text-white/40">
                                                    {c.origen === 'real' ? 'se mira al simular' : c.tiene_rutina_cargada ? 'cargada' : 'sin cargar'}
                                                </p>
                                            </td>
                                            <td className="px-2.5 py-2.5">
                                                {c.reportes.length ? c.reportes.map((r) => <Chip key={r}>{r}</Chip>) : <Chip apagado>ninguno</Chip>}
                                            </td>
                                            <td className="px-2.5 py-2.5">
                                                <span className={`text-xs px-2 py-0.5 rounded-full ${c.estado === 'Activo'
                                                    ? 'bg-emerald-500/15 text-emerald-400' : 'bg-white/10 text-white/60'}`}>{c.estado}</span>
                                                {c.en_pausa && <p className="text-[11px] text-amber-400 mt-1">en pausa</p>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>)}
            </Paso>

            <div ref={pasoMomento} className="scroll-mt-20">
            <Paso n={2} titulo="Elige el momento">
                {elegido && (
                    <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm mb-5 pb-4 border-b border-[#1E1E1E]" data-testid="sim-elegido">
                        <p><span className="text-white/40">Cliente:</span> <span className="text-white font-medium">{elegido.nombre}</span></p>
                        <p><span className="text-white/40">Plan:</span> <span className="text-white">{elegido.plan_nombre}</span></p>
                        <p><span className="text-white/40">Rutina:</span> <span className="text-white">{RUTINA[elegido.rutina] || elegido.rutina}</span></p>
                        <p><span className="text-white/40">Entrenador:</span> <span className="text-white">{elegido.entrenador ? 'Sí' : 'No'}</span></p>
                        <p><span className="text-white/40">Estado:</span> <span className="text-white">{elegido.estado}</span></p>
                    </div>
                )}
                <div className="flex flex-wrap items-end gap-4 sm:gap-6">
                    <div>
                        <label className="block text-xs uppercase tracking-wider text-white/40 mb-1.5" htmlFor="sim-inicio">Fecha de inicio</label>
                        <input id="sim-inicio" type="date" value={inicio} data-testid="sim-inicio"
                            onChange={(e) => { setInicio(e.target.value); limpiar(); }} className={`${ENTRADA} h-11`} />
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-wider text-white/40 mb-1.5">Simular por</p>
                        <Pestanas testid="sim-por" valor={porFecha ? 'fecha' : 'semana'} onCambio={cambiarPor}
                            opciones={[['semana', 'Semana y día'], ['fecha', 'Fecha']]} />
                    </div>
                </div>
                <div className="flex flex-wrap items-end gap-4 sm:gap-6 mt-4">
                    {porFecha ? (
                        <div>
                            <label className="block text-xs uppercase tracking-wider text-white/40 mb-1.5" htmlFor="sim-fecha">Fecha simulada</label>
                            <input id="sim-fecha" type="date" value={fecha} data-testid="sim-fecha"
                                onChange={(e) => { setFecha(e.target.value); limpiar(); }} className={`${ENTRADA} h-11`} />
                        </div>
                    ) : (<>
                        <Selector etiqueta="Semana" testid="sim-semana" valor={`Semana ${semana}`}
                            puedeMenos={semana > 1} puedeMas={semana < semanaMax}
                            onMenos={() => { setSemana((s) => Math.max(1, s - 1)); limpiar(); }}
                            onMas={() => { setSemana((s) => Math.min(semanaMax, s + 1)); limpiar(); }} />
                        <Selector etiqueta="Día del plan" testid="sim-dia" valor={`Día ${dia}`}
                            puedeMenos={dia > 1} puedeMas={dia < 7}
                            onMenos={() => { setDia((d) => Math.max(1, d - 1)); limpiar(); }}
                            onMas={() => { setDia((d) => Math.min(7, d + 1)); limpiar(); }} />
                    </>)}
                    <label className="flex items-center gap-2 text-sm text-white/70 h-11 cursor-pointer select-none">
                        <input type="checkbox" checked={mandaReportes} data-testid="sim-manda-reportes"
                            onChange={(e) => { setMandaReportes(e.target.checked); limpiar(); }}
                            className="w-4 h-4 accent-[#FF671F]" />
                        Manda los reportes en cuanto se abren
                    </label>
                    <Button onClick={simular} disabled={!elegido || simulando} data-testid="sim-simular"
                        className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white font-bold uppercase tracking-wider px-8 h-11">
                        <Play className="w-4 h-4 mr-2" />{simulando ? 'Simulando…' : 'Simular'}
                    </Button>
                </div>
                <div className="mt-4">
                    <CasillaDeCorreo donde={datos.correo_de_prueba} marcada={mandarCorreos} desactivada={simulando}
                        onCambio={(v) => { setMandarCorreos(v); limpiar(); }} />
                </div>
                {fechaElegida && (
                    <p className="text-xs text-white/35 mt-3" data-testid="sim-fecha-elegida">
                        Se simulará el <span className="text-white/60">{fechaLarga(fechaElegida)}</span>.
                    </p>
                )}
            </Paso>
            </div>

            {resultado && <Resultado r={resultado} />}
            </>)}
        </div>
    );
};

const Resultado = ({ r }) => {
    const m = r.momento;
    const ac = r.activecampaign;
    return (
        <div className="space-y-5" data-testid="sim-resultado">
            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3" data-testid="sim-dry-run">
                <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0" />
                <p className="text-emerald-300 font-semibold uppercase tracking-wide text-sm">Dry run</p>
                <p className="text-emerald-100/80 text-sm">
                    {r.correos_mandados
                        ? 'Ninguna acción se ha ejecutado sobre el cliente: no se ha guardado nada. Solo se ha intentado el correo de prueba de abajo.'
                        : 'Ninguna acción ha sido ejecutada: no se ha guardado, enviado ni llamado a nada.'}
                </p>
            </div>
            <EnvioDePrueba envio={r.correos_mandados} />

            <Card className="bg-[#111] border-[#FF671F]/40">
                <CardContent className="p-4 sm:p-5">
                    <p className="text-xs uppercase tracking-wider text-[#FF671F] mb-3">Contexto</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm" data-testid="sim-contexto">
                        <Dato t="Cliente" v={r.cliente.nombre} />
                        <Dato t="Plan" v={r.cliente.plan_nombre} />
                        <Dato t="Fecha de inicio" v={m.fecha_inicio} />
                        <Dato t="Fecha simulada" v={m.fecha_simulada} />
                        <Dato t="Semana · día del plan" v={m.semana ? `Semana ${m.semana} · día ${m.dia}` : 'antes del inicio'} />
                        <Dato t="Día del calendario" v={m.dia_calendario} />
                        <Dato t="Semana del ciclo" v={m.semanas_ciclo ? `${m.semana_plan} de ${m.semanas_ciclo}${m.vuelta > 1 ? ` · ciclo ${m.vuelta}` : ''}${m.plan_vencido ? ' · Plan vencido' : ''}` : `${m.semana_plan}`} />
                        <Dato t="Semana de reporte" v={`${m.semana_reporte ?? '—'} · de ${m.fuente_semana_reporte}`} />
                        <Dato t="Semana de rutina" v={m.semana_rutina ?? 'sin rutina'} />
                        <Dato t="Tipo de día" v={m.tipo_de_dia === 'entrenamiento' ? 'Entreno' : 'Descanso'} />
                        <Dato t="Acceso a la app" v={m.acceso?.activo ? 'Sí' : `No (${m.acceso?.motivo})`} />
                        <Dato t="Rutina" v={RUTINA[r.cliente.rutina] || r.cliente.rutina} />
                    </div>
                    {m.avisos?.length > 0 && (
                        <div className="mt-3 space-y-1">
                            {m.avisos.map((a, i) => (
                                <p key={i} className="text-xs text-amber-300 flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{a}</p>
                            ))}
                        </div>
                    )}
                    <p className="text-xs text-white/35 mt-3">
                        {fechaLarga(m.fecha_simulada)}.
                    </p>
                </CardContent>
            </Card>

            <div className="grid lg:grid-cols-5 gap-5">
                <Card className="bg-[#111] border-[#222] lg:col-span-3">
                    <CardContent className="p-4 sm:p-5">
                        <h3 className="text-white font-bold uppercase mb-1" style={{ fontFamily: 'Barlow Condensed', fontSize: 20 }}>
                            Acciones que se ejecutarían
                        </h3>
                        {r.acciones.length === 0 ? (
                            <p className="text-white/50 text-sm">Ese día ninguna regla produce nada para este cliente.</p>
                        ) : (
                            <ol className="space-y-3" data-testid="sim-acciones">
                                {r.acciones.map((a, i) => {
                                    const para = PARA[a.para] || PARA.cliente;
                                    const Icono = para.icono;
                                    return (
                                        <li key={i} className="flex gap-3">
                                            <span className="w-[56px] sm:w-[72px] shrink-0 text-right text-sm font-semibold text-white/80 pt-0.5">{a.hora}</span>
                                            <div className="flex-1 min-w-0 border-l border-[#2A2A2A] pl-3 pb-1">
                                                <div className="flex items-start gap-2">
                                                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-1" />
                                                    <p className="text-white">
                                                        <span className="font-medium mr-2">{a.que}</span>
                                                        <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full align-middle mr-1 ${para.clase}`}>
                                                            <Icono className="w-3 h-3" />{para.texto}
                                                        </span>
                                                        <span className="inline-block text-[10px] px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-300 align-middle tracking-wider">{a.estado}</span>
                                                    </p>
                                                </div>
                                                <p className="text-xs text-white/50 mt-0.5">{a.canal} · {a.motivo}</p>
                                                <p className="text-xs text-white/35 mt-0.5">{a.ejecucion}</p>
                                                <p className="text-[11px] text-white/30 mt-0.5 font-mono break-words">{a.regla}</p>
                                                <TextoDelCorreo correo={a.correo} abierto />
                                            </div>
                                        </li>
                                    );
                                })}
                            </ol>
                        )}
                        {r.equipo.length > 0 && (
                            <div className="mt-5 pt-4 border-t border-[#1E1E1E]">
                                <p className="text-xs uppercase tracking-wider text-white/40 mb-2">Además, ese día el equipo tiene (para todos los clientes)</p>
                                {r.equipo.map((t, i) => (
                                    <p key={i} className="text-sm text-white/70">· {t.que} <span className="text-white/35">({t.quien})</span></p>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card className="bg-[#111] border-[#222] lg:col-span-2">
                    <CardContent className="p-4 sm:p-5">
                        <h3 className="text-white font-bold uppercase mb-3" style={{ fontFamily: 'Barlow Condensed', fontSize: 20 }}>
                            Reglas evaluadas
                        </h3>
                        <ul className="space-y-4" data-testid="sim-reglas">
                            {r.reglas.map((x, i) => (
                                <li key={i} className="flex gap-2.5">
                                    {x.aplica
                                        ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                                        : <Circle className="w-4 h-4 text-white/25 shrink-0 mt-0.5" />}
                                    <div className="min-w-0">
                                        <p className={`text-sm font-medium ${x.aplica ? 'text-white' : 'text-white/60'}`}>{x.nombre}</p>
                                        <p className="text-xs text-white/45">{x.resultado}</p>
                                        {x.condiciones?.length > 0 && (
                                            <ul className="mt-1.5 space-y-0.5">
                                                {x.condiciones.map((c, j) => (
                                                    <li key={j} className="flex items-start gap-1.5 text-xs">
                                                        {c.cumple
                                                            ? <CheckCircle2 className="w-3 h-3 text-emerald-400/80 shrink-0 mt-0.5" />
                                                            : <XCircle className="w-3 h-3 text-white/30 shrink-0 mt-0.5" />}
                                                        <span className={c.cumple ? 'text-white/70' : 'text-white/40'}>{c.texto}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        <p className="text-[11px] text-white/25 font-mono break-words mt-1">{x.regla}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </CardContent>
                </Card>
            </div>

            {ac && <ActiveCampaign ac={ac} />}

            <div className="grid md:grid-cols-2 gap-5">
                <Lista titulo="Lo que se da por hecho" items={r.supuestos} />
                <Lista titulo="Lo que no se simula" items={r.no_simulado} />
            </div>
        </div>
    );
};

const ActiveCampaign = ({ ac }) => {
    const auto = ac.automatizacion || {};
    return (
        <Card className="bg-[#111] border-[#222]" data-testid="sim-activecampaign">
            <CardContent className="p-4 sm:p-5 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-white font-bold uppercase flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed', fontSize: 20 }}>
                        <Send className="w-4 h-4 text-white/60" /> ActiveCampaign
                    </h3>
                    <span className="text-[11px] px-2 py-0.5 rounded border border-amber-500/40 text-amber-300 tracking-wider">{ac.estado}</span>
                </div>
                <p className="text-sm text-white/80">
                    {ac.escrituras.length === 0
                        ? 'Ninguna regla de la app manda nada a ActiveCampaign: ni contactos, ni tags, ni campos, ni eventos.'
                        : `${ac.escrituras.length} escrituras`}
                    {' '}<span className="text-white/50">{ac.nota}</span>
                </p>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div className="rounded-lg border border-[#222] bg-[#0E0E0E] p-3">
                        <p className="text-xs uppercase tracking-wider text-white/40 mb-1.5">Lo único que hace la app con AC: leer</p>
                        <p className="text-white/70 text-xs">{ac.lectura.que}.</p>
                        <p className="text-white/50 text-xs mt-1">{ac.lectura.cuando}. {ac.lectura.para_que}.</p>
                        <p className="text-emerald-300/80 text-xs mt-1">{ac.lectura.en_simulacion}</p>
                        {ac.ciclo_ac_guardado && (
                            <p className="text-white/60 text-xs mt-2">
                                Última lectura guardada: semana {ac.ciclo_ac_guardado.semana} de {ac.ciclo_ac_guardado.largo || 4}
                                {ac.ciclo_ac_guardado.stop ? ' · STOP' : ''} · leída el {String(ac.ciclo_ac_guardado.leido_en || '').slice(0, 10)}
                                {' '}({!ac.ciclo_ac_fresco_hoy ? 'no fresca: manda la cuenta de la app'
                                    : ac.quien_manda === 'reloj' ? 'fresca, pero aquí manda el reloj de la app'
                                    : ac.quien_manda === 'app' ? 'fresca, pero sin clave de ActiveCampaign no manda'
                                    : 'fresca hoy: manda sobre la semana de reporte'})
                            </p>
                        )}
                    </div>
                    <div className="rounded-lg border border-[#222] bg-[#0E0E0E] p-3">
                        <p className="text-xs uppercase tracking-wider text-white/40 mb-1.5">Lo que haría la automatización de AC (no la app)</p>
                        {!auto.aplica ? (
                            <p className="text-white/50 text-xs">{auto.motivo}</p>
                        ) : (<>
                            <p className="text-white/70 text-xs">
                                Semana en AC: {auto.al_empezar_el_dia.semana} al empezar el día → {auto.al_acabar_el_dia.semana} al acabarlo
                                {auto.al_acabar_el_dia.reporte_mensual ? ` · reporte mensual: ${auto.al_acabar_el_dia.reporte_mensual}` : ''}
                                {auto.al_acabar_el_dia.reporte_quincenal ? ` · quincenal: ${auto.al_acabar_el_dia.reporte_quincenal}` : ''}
                            </p>
                            {auto.cambios.length > 0 ? (
                                <ul className="mt-1.5 space-y-0.5" data-testid="sim-ac-cambios">
                                    {auto.cambios.map((c, i) => (
                                        <li key={i} className="text-xs text-white/60">
                                            <span className="text-white/80 font-medium">{String(c.cuando).slice(11, 16)}</span> · {c.motivo}
                                            <span className="text-white/35"> ({c.campo}: {String(c.antes)} → {String(c.despues)})</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : <p className="text-white/40 text-xs mt-1">Ese día no cambia nada en AC.</p>}
                            <p className="text-[11px] text-white/30 mt-2 font-mono">core/reloj.py · contrato de AC replicado ({ac.quien_manda === 'reloj' ? 'aquí manda el reloj' : 'en sombra'})</p>
                        </>)}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

const Dato = ({ t, v }) => (
    <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wider text-white/40">{t}</p>
        <p className="text-white font-medium break-words">{v}</p>
    </div>
);

const Lista = ({ titulo, items }) => (
    <Card className="bg-[#0E0E0E] border-[#1E1E1E]">
        <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-white/40 mb-2">{titulo}</p>
            <ul className="space-y-1.5">
                {items.map((t, i) => <li key={i} className="text-xs text-white/55">· {t}</li>)}
            </ul>
        </CardContent>
    </Card>
);

export default AdminSimuladorPage;
